export type ComponentTypes =
  | HandoffComponentTypes
  | 'article'
  | 'article_suggestion'
  | 'buttons'
  | 'csat_survey'
  | 'DATE'
  | 'DROPDOWN'
  | 'EMAIL'
  | 'form'
  | 'hyperlink_redirect'
  | 'LONG_TEXT'
  | 'open_url'
  | 'PHONE_NUMBER'
  | 'PHONE'
  | 'NUMBER'
  | 'restart_conversation'
  | 'SHORT_TEXT'
  | 'CHECKBOX'
  | 'text_message'
  | 'ujet_chat_handoff'
  | 'zendesk_attachment_upload'
  | 'attachment_upload'
  | 'LIST'
  | 'trigger_event'
  | 'MULTI_SELECT_LIST'
  | 'visible_only_to_admins'
  | 'image'
  | 'embed'
  | 'forethought_live_chat_handoff'
  | 'sunco_live_chat'
  | 'user_message'
  | 'dynamic_card'
  | 'knowledge_retrieval_stream'
  | 'csat_trigger_point'
  | 'event_message'
  | 'interactive_email_inquiry'
  | 'end_interactive_email_chat'
  | 'attachment_analysis_upload';

export type HandoffComponentTypes =
  | 'brand_embassy_chat_handoff'
  | 'drift_chat_handoff'
  | 'intercom_chat_handoff'
  | 'kustomer_chat_handoff'
  | 'salesforce_chat_handoff'
  | 'snapengage_chat_handoff'
  | 'zendesk_chat_handoff'
  | 'zendesk_messaging_handoff'
  | 'generic_handoff'
  | 'gorgias_chat_handoff'
  | 'freshchat_chat_handoff'
  | 'salesforce_messaging_handoff'
  | 'live_chat_chat_handoff';

export type HandoffIntegrations =
  | 'brand_embassy'
  | 'drift'
  | 'generic'
  | 'intercom'
  | 'kustomer'
  | 'salesforce'
  | 'snap_engage'
  | 'zendesk'
  | 'zendesk_messaging'
  | 'ujet'
  | 'gorgias'
  | 'freshchat'
  | 'salesforce_messaging'
  | 'live_chat'
  | 'forethought_live_chat'
  | 'sunco-live-chat'
  | 'zendesk-zopim-live-chat'
  | 'intercom-live-chat'
  | 'freshchat-live-chat'
  | 'kustomer-live-chat';

export type GenericHandoffWidgetOption = 'hide' | 'minimize' | 'nothing';

export type TrackingEventTypes =
  | 'widget-opened'
  | 'widget-customer-agent-chat'
  | 'widget-customer-article-suggestion-helped'
  | 'widget-customer-article-suggestion-unhelpful'
  | 'widget-customer-ask-question'
  | 'widget-customer-entered-intent-by-button'
  | 'widget-customer-entered-intent-by-proactive-bubble'
  | 'widget-customer-closed-proactive-bubbles'
  | 'widget-customer-proactive-bubbles-shown'
  | 'widget-customer-handoff'
  | 'go-to-article'
  | 'widget-csat-submitted'
  | 'widget-zendesk-update-settings'
  | 'widget-zendesk-attachment-upload'
  | 'widget-disabled-by-local-storage'
  | 'widget-csat-opened'
  | 'widget-banner-image-clicked'
  | 'widget-banner-image-closed'
  | 'widget-handoff-auth-failure'
  | 'widget-error-banner-visible';

export type HandoffConfirmation = 'yes_resolve' | 'no_resolve';

export type LaunchStyle = 'icon' | 'floating_bar';

export type Size = 'standard' | 'large';

export type Placement = 'bottom_right' | 'bottom_left';

export type Font = 'Plus Jakarta Sans' | string;

export type SolveError =
  | string
  | ConversationNotFoundError
  | AttemptedToFetchRedactedConversationError
  | CustomFetchErrorObject
  | null;

interface WorkflowConfigArgs {
  banner_image_config: {
    alt_text: string | null;
    image: string | null;
    is_enabled: boolean;
    link: string | null;
  };
  colors?: {
    background: string | null;
    danger: string | null;
    theme: string | null;
  };
  config_params: ContextVariables;
  conversation_id: string;
  csat_config: CsatConfigRevamped;
  csat_survey_config: {
    is_enabled: boolean;
    num_of_steps_required: number;
    should_only_show_on_close: boolean;
  };
  disable_forethought_branding: boolean;
  display_proactive_chat: boolean;
  display_proactive_chat_intents: boolean;
  font: Font;
  forethought_text?: {
    additional_feedback_dialog_title: string;
    close_button_title: string;
    free_form_input_placeholder: string;
    free_form_input_placeholder_in_progress: string;
    free_form_input_placeholder_not_started: string;
    sources: string;
    submit_button_title: string;
    tag_selection_button_greeting_message: string;
    tag_selection_greeting_message: string;
  };
  free_form_intent_detection_enabled?: boolean;
  greeting_line?: string;
  images?: {
    agent_chat: string | null;
    header: string | null;
    help_button_hovered: string | null;
  };
  initial_widget_components?: WidgetComponent[];
  intentId: string | null | undefined;
  interactive_email_static_text?: {
    cancel: string;
    end_chat: string;
    end_chat_description: string;
    feedback_description: string;
    is_issue_resolved: string;
    no: string;
    yes: string;
  };
  intro_config: {
    image: {
      enabled: boolean;
      value: string;
    };
    subtitle: {
      enabled: boolean;
      value: string;
    };
    title: {
      enabled: boolean;
      value: string;
    };
  };
  launch_style: LaunchStyle;
  logrocket_enabled: boolean;
  on_load_config: OnLoadConfig;
  persistence_duration_minutes: number;
  persistence_enabled: boolean;
  placement: Placement;
  privacy_consent_config: PrivacyConsentConfig | null;
  proactive_chat_greeting: string;
  quick_feedback_config: {
    feedback_prompt: string;
    hover_label_bad: string;
    hover_label_good: string;
    is_enabled: boolean;
    negative_routing_intent_workflow_id: string | null;
    question_for_high_ratings: string;
    question_for_low_ratings: string;
    should_request_feedback_for_high_ratings: boolean;
    should_request_feedback_for_low_ratings: boolean;
    style: 'thumbs' | 'bw_smile' | 'hearts';
  };
  size: Size;
  tag_selection_enabled: boolean;
  text?: {
    tab_name: string | null;
  };
  top_n_intents?: Array<{
    href?: string | null;
    intent_id: string;
    intent_name: string;
  }>;
  use_help_button_image_as_icon?: boolean;
  widget_enabled: boolean;
  workflow_params?: ContextVariables;
  workflow_tags?: Array<string>;
}

interface WorkflowConfigResponse extends WorkflowConfigArgs {
  enabled_feature_flags: string[];
}

interface PrivacyConsentConfig {
  background_color: string;
  call_to_action_label: string;
  is_banner_dismissible: boolean;
  placement: 'top' | 'bottom';
  privacy_policy: string;
  prompt_header: string;
  show_when_conversation_starts: boolean;
  style: 'call_to_action' | 'sticky_banner';
}

interface CsatConfigRevamped {
  is_enabled: boolean;
  negative_rating_routing_intent?: string;
  no_resolve_option: string;
  one_to_five_labels: string[];
  options_for_resolution_confirmation: string[];
  question_for_additional_feedback: string;
  question_for_high_ratings: string;
  question_for_low_ratings: string;
  question_for_resolution_confirmation: string;
  rating_question: string;
  reasons_for_high_ratings: string[];
  reasons_for_low_ratings: string[];
  scale: 'one_to_two' | 'one_to_five';
  should_request_additional_feedback: boolean;
  should_request_feedback_for_high_ratings: boolean;
  should_request_feedback_for_low_ratings: boolean;
  should_request_resolution_confirmation: boolean;
  should_show_submit_confirmation_message: boolean;
  should_use_rating_based_submit_message: boolean;
  show_on_header: boolean;
  show_when_agent_chat_ends: boolean;
  show_when_widget_minimizes: boolean;
  style: 'star' | 'smile' | 'bw_smile' | 'thumbs';
  submit_confirmation_message: string;
  submit_message_for_high_ratings: string;
  submit_message_for_low_ratings: string;
  yes_resolve_option: string;
}

export interface ContextVariableConfigurationFields {
  is_alphanumeric?: boolean;
  max_length?: number;
  min_length?: number;
}

interface FormFields {
  configuration_fields: ContextVariableConfigurationFields | null;
  dropdown_options?: Array<DropdownItem>;
  input_type: ComponentTypes;
  list_type_options?: ListOption[];
  output_variable: string;
  placeholder: string;
  pre_populated_value?: string;
  prepopulated_field?: string;
}

interface ContextVariables {
  [key: string]: string | boolean | number | string[];
}

export interface ArticleStepData {
  current_step_id: string;
  intent_workflow_id: string;
  intent_workflow_version: number;
  org_id: number;
  solve_widget_product?: string;
}

interface ButtonOptions {
  display_name: string;
  url?: string | null;
  url_config?: {
    target_attribute: '_blank' | '_self' | '_parent' | '_top';
  } | null;
  value: string;
}

interface BaseWidgetComponent {
  context_variables?: ContextVariables;
  is_sent?: boolean;
}

interface FormStepComponent extends BaseWidgetComponent {
  component_fields: {
    forethought_text: FormStepComponentText;
    form_fields: Array<FormFields>;
    prompt: string;
    quotedMessageId?: string; // Used by Sunco One Chat only
  } & Pick<
    UserMessageComponent['component_fields'],
    | 'agentIcon'
    | 'agentName'
    | 'should_render_as_markdown'
    | 'should_render_single_new_line_characters'
  >;
  component_type: ComponentTypes;
  delay?: number;
  hideTimestamp?: boolean;
  isInAgentConvo?: boolean;
  isReroutedByNegativeFeedback?: boolean;
  isStreamingComponent?: boolean;
  language?: string;
  neverMindSelection?: string;
  query?: string;
  should_render_as_markdown?: boolean;
  timestamp?: number;
}

interface FormStepComponentText {
  checkbox_no: string;
  checkbox_placeholder: string;
  checkbox_yes: string;
  dropdown_back: string;
  email_error: string;
  never_mind: string;
  number_error: string;
  phone_error: string;
  required_error: string;
  required_label: string;
  short_text_alphanumeric_error: string;
  short_text_equal_error: string;
  short_text_max_error: string;
  short_text_min_error: string;
  submit_button: string;
}

export type ReadArticleActions = 'new_tab' | 'expand_in_widget' | 'none';

interface ArticleCarouselComponent extends BaseWidgetComponent {
  component_fields: {
    article_step_data?: ArticleStepData;
    forethought_text: {
      agatha_message: string;
      read_article_text: string;
      read_more_article_text: string;
      read_more_articles_text: string;
      related_articles_text: string;
    };
    knowledge_articles: Array<ArticleComponent>;
    read_article_action?: ReadArticleActions;
    text_message_override?: string;
  };
  component_type: ComponentTypes;

  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isStreamingComponent?: boolean;
  language?: string;
  query?: string;
}

interface ButtonComponent extends BaseWidgetComponent {
  component_fields: {
    hide_free_form_query?: boolean;
    multi_select: boolean;
    options: Array<ButtonOptions>;
    output_variable: string;
    prompt: string;
    should_confirm_tag?: boolean;
  } & Pick<
    UserMessageComponent['component_fields'],
    | 'agentIcon'
    | 'agentName'
    | 'author'
    | 'should_render_as_markdown'
    | 'should_render_single_new_line_characters'
  >;
  component_type: ComponentTypes;
  delay?: number;
  hideTimestamp?: boolean;
  isInAgentConvo?: boolean;
  isReroutedByNegativeFeedback?: boolean;
  isStreamingComponent?: boolean;
  language?: string;
  query?: string;
  timestamp?: number;
}

type Metadata = {
  description: string;
  image: string;
  title: string;
  url: string;
};

type Nullable<T> = { [P in keyof T]: T[P] | null };

export interface FileAttachment {
  content_type?: string | null;
  file_extension?: string | null;
  filesize?: number | null;
  name: string;
  url: string;
}

interface UserMessageComponent extends BaseWidgetComponent {
  component_fields: {
    agentIcon?: string;
    agentName?: string;
    article_step_data?: ArticleStepData;
    articles?: LiveChatArticle[];
    attachments?: FileAttachment[];
    author?: string;
    knowledge_articles?: NewIrArticleComponent[];
    message: string;
    metadata?: Nullable<Metadata>;
    read_article_action?: ReadArticleActions;
    should_render_as_markdown?: boolean;
    should_render_single_new_line_characters?: boolean;
  };
  component_type: ComponentTypes;
  contextVariables?: ContextVariables;
  delay?: number;
  hideTimestamp?: boolean;
  isInAgentConvo?: boolean;
  isReroutedByNegativeFeedback?: boolean;
  isStreamingComponent?: boolean;
  language?: string;
  query?: string;
  timestamp?: number;
}

interface InteractiveEmailInquiryComponent {
  component_fields: {
    source_body: string;
    source_doc_type: string;
    source_id: string;
    source_title: string;
  };
  component_type: 'interactive_email_inquiry';
}

interface IntercomAutoSendConfig {
  auto_send_timeout: number;
  should_auto_send_message: boolean;
}

type IntercomChatAutoSendConfig = IntercomAutoSendConfig | null;

type BrandEmbassyCustomFields = {
  [key: string]: string;
} | null;

export type KustomerCustomAttributes = Record<string, unknown>;

// See https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomer-create-conversation#callbackresponse
interface KustomerCallbackResponse {
  conversationId: string;
  createdAt: string;
  ended: boolean;
  isInAssistantMode: boolean;
}

interface AdditionalParameters {
  [key: string]: unknown;
}

interface FormattedSalesforceExtraPrechatFormDetails {
  displayToAgent: boolean;
  label: string;
  transcriptFields: string[];
  value: string;
}

type SalesforceVisibleFields = Record<
  string,
  { isEditableByEndUser: boolean; value: string }
>;

export interface BaseArticleComponent {
  document_id: string;
  full_html?: string | null;
  link: string | null;
  quick_feedback_id?: string | null;
  span_id?: string;
  standardized_document_id?: string;
  title: string;
  trace_id?: string;
}

interface ArticleComponent extends BaseArticleComponent {
  confidence_level?: string;
  confidence_score?: number;
  date_created?: string;
  date_updated?: string;
  preview: string;
  source_type: string;
}

interface NewIrArticleComponent extends BaseArticleComponent {
  full_html: string;
  link: string;
  standardized_article_id: string;
}

interface OpenUrlComponent {
  component_fields: {
    url: string;
  };
  component_type: ComponentTypes;
}

interface ZendeskAttachmentUploadStepComponent extends BaseWidgetComponent {
  component_fields: {
    forethought_text: AttachmentStepText;
    previous_attachment_tokens: string;
    required: boolean;
    should_use_s3?: boolean;
    upload_url: string;
  };
  component_type: ComponentTypes;
  delay?: number;
  file_names?: Array<string>;
  isReroutedByNegativeFeedback?: boolean;
  isStreamingComponent?: boolean;
  neverMindSelection?: string;
  query?: string;
}

export interface AttachmentStepText {
  add_file: string;
  form_title: string;
  form_title_required: string;
  never_mind: string;
  skip_attachment: string;
  submit_button: string;
}

interface AttachmentUploadStepComponent extends BaseWidgetComponent {
  component_fields: {
    forethought_text: AttachmentStepText;
    max_individual_size?: number | null;
    max_total_size: number;
  };
  component_type: ComponentTypes;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isStreamingComponent?: boolean;
}

interface AttachmentAnalysisUploadStepComponent extends BaseWidgetComponent {
  component_fields: {
    forethought_text: AttachmentStepText;
    num_files: number;
    output_variable: string;
    prompt: string;
  };
  component_type: ComponentTypes;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isStreamingComponent?: boolean;
}

const isAttachmentAnalysisUploadStepComponent = (
  widgetComponent:
    | AttachmentAnalysisUploadStepComponent
    | AttachmentUploadStepComponent
    | ZendeskAttachmentUploadStepComponent,
): widgetComponent is AttachmentAnalysisUploadStepComponent => {
  return widgetComponent.component_type === 'attachment_analysis_upload';
};

interface EventMessageComponent extends BaseWidgetComponent {
  component_fields: {
    message: string;
  };
  component_type: 'event_message';
  delay?: number;
  isConversationResolver?: boolean;
  isReroutedByNegativeFeedback?: boolean;
  isStreamingComponent?: boolean;
  query?: string;
  timestamp?: number;
}

type WidgetComponent =
  | FormStepComponent
  | ButtonComponent
  | ArticleCarouselComponent
  | UserMessageComponent
  | SelectIntentComponent
  | ZendeskAttachmentUploadStepComponent
  | DynamicCardStepComponent
  | EventMessageComponent;

interface InitConversationInterface {
  conversation_id: string;
  next_step_id: string;
  transcript_length: number;
  widget_components: Array<WidgetComponent>;
}

interface ConversationNotFoundError {
  message: string;
  status: 404;
}

interface BadRequestError {
  message: string;
  status: 400;
}

interface TooManyRequestsError {
  message: string;
  status: 429;
}

type CustomFetchErrorObject = {
  message: string;
  method: string;
  response_data: unknown;
  status: number;
  url: string;
};

interface AttemptedToFetchRedactedConversationError {
  message: string;
  status: 400;
}

export const isConversationNotFoundError = (
  data: unknown,
): data is ConversationNotFoundError => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'status' in data &&
    data.status === 404
  );
};

export const isBadRequestError = (data: unknown): data is BadRequestError => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'status' in data &&
    data.status === 400 &&
    'message' in data &&
    typeof data.message === 'string' &&
    data.message.startsWith('Invalid request')
  );
};

export const isTooManyRequestsError = (
  data: unknown,
): data is TooManyRequestsError => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'status' in data &&
    data.status === 429
  );
};

export const isAttemptedToFetchRedactedConversationError = (
  data: unknown,
): data is AttemptedToFetchRedactedConversationError => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'status' in data &&
    data.status === 400 &&
    'message' in data &&
    typeof data.message === 'string' &&
    data.message.startsWith('Attempted to fetch redacted conversation with id')
  );
};

interface CsatData {
  additional_feedback?: string;
  csat_feedback?: string[];
  csat_resolve?: boolean;
  csat_score?: number;
  helpdesk_conversation_id?: string;
  submitted: boolean;
}

interface CsatFormData {
  csatFeedback: string[];
  csatResolve?: boolean;
  csatScore: 0 | 1 | 2 | 3 | 4 | 5;
  isCsatDataSubmitted: boolean;
}

interface CsatConfig {
  csatType: 'close' | 'inline';
  didSubmit: boolean;
  isCsatDetailVisible: boolean;
  isCsatEndTransition?: boolean;
  isCsatSurveyFinished?: boolean;
  isCsatVisible?: boolean;
}

interface CsatStates {
  actionToExecuteAfterCsatDismiss:
    | 'end-live-chat'
    | 'resume-from-trigger-point'
    | 'handle-live-chat-resolved'
    | null;
  additionalFeedback: string;
  currentStage: number;
  hasDismissed: boolean;
  hasFeedbackTooltipPrompted: boolean;
  hasSubmitted: boolean;
  highRatingReasonIndices: number[];
  isCsatVisible: boolean;
  isEditing: boolean;
  isPromptDisabledForMinimize: boolean;
  isResolved?: boolean;
  lowRatingReasonIndices: number[];
  rating: number;
  submittedRating?: number;
  triggeredFrom: WidgetCsatOpenedTrackingEvent['triggered_from'];
}

type CsatTranslatedText = {
  text: Partial<{
    CSAT_BAD: string;
    CSAT_CHANGE_RATING: string;
    CSAT_CLOSE: string;
    CSAT_DID_NOT_UNDERSTAND: string;
    CSAT_EASY: string;
    CSAT_EXCELLENT: string;
    CSAT_FRIENDLY_TONE: string;
    CSAT_GOOD: string;
    CSAT_GOOD_SOLUTION: string;
    CSAT_IMPERSONAL: string;
    CSAT_OKAY: string;
    CSAT_PERSONALIZED: string;
    CSAT_QUICK_TURNAROUND: string;
    CSAT_RATING_QUESTION: string;
    CSAT_RESOLVE_QUESTION: string;
    CSAT_SKIP_AND_CLOSE: string;
    CSAT_SUBMIT: string;
    CSAT_TELL_US_MORE: string;
    CSAT_TERRIBLE: string;
    CSAT_THANK_YOU: string;
    CSAT_TOOK_TOO_LONG: string;
    CSAT_UNDERSTOOD_NEEDS: string;
    CSAT_UNFRIENDLY: string;
    CSAT_UNHELPFUL: string;
    CSAT_UNHELPFUL_RESPONSE: string;
    CSAT_YOU_RATED: string;
    FT_NO: string;
    FT_YES: string;
  }>;
};

type ApiHeaders = {
  Authorization: string;
  'is-config-preview': boolean;
  'is-draft': boolean;
  'is-preview': boolean;
  'solve-origin': string;
  'workflow-config-version'?: string;
};

interface DropdownItem {
  //dropdown options
  dropdown_options?: Array<DropdownItem>;
  //label for the dropdown option
  label: string;
  //value for the dropdown option
  value: string;
}

interface SendTrackingActionCommonArgs {
  channel: 'live' | 'preview';
  conversationId?: string;
  event_type: TrackingEventTypes;
  product: 'flamethrower';
  sessionId?: string;
}

interface BaseTrackingEvent {
  event_type: TrackingEventTypes;
}

interface WidgetOpenedTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-opened';
  mechanism:
    | 'manual-click-on-widget-icon'
    | 'manual-click-on-proactive-greeting'
    | 'manual-click-on-proactive-intent-button'
    | 'js-api-widget-open'
    | 'js-api-widget-launchQuery'
    | 'full-screen-true';
}

interface WidgetCsatSurveyTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-csat-submitted';
  triggered_from: WidgetCsatOpenedTrackingEvent['triggered_from'];
}

interface WidgetCsatOpenedTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-csat-opened';
  triggered_from:
    | 'minimize'
    | 'csat-trigger-point'
    | 'end-live-chat'
    | 'header-button-clicked'
    | null;
}

interface WidgetCustomerAskQuestionTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-customer-ask-question';
}

interface WidgetCustomerClosedProactiveBubblesTrackingEvent
  extends BaseTrackingEvent {
  event_type: 'widget-customer-closed-proactive-bubbles';
}

interface WidgetCustomerProactiveBubblesShownTrackingEvent
  extends BaseTrackingEvent {
  event_type: 'widget-customer-proactive-bubbles-shown';
}

interface WidgetBannerImageClickedTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-banner-image-clicked';
}

interface WidgetBannerImageClosedTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-banner-image-closed';
}

interface WidgetErrorBannerVisibleTrackingEvent extends BaseTrackingEvent {
  conversation_id: string;
  error_message: string;
  event_type: 'widget-error-banner-visible';
  is_preview: boolean;
  method?: string;
  response_data?: unknown;
  status?: number;
  url?: string;
}

interface GoToArticleTrackingEvent extends BaseTrackingEvent {
  current_step_id?: string;
  doc_id: string;
  doc_index: number;
  event_type: 'go-to-article';
  intent_workflow_id?: string;
  intent_workflow_version?: number;
  org_id?: number;
  span_id?: string;
  standardized_document_id?: string;
  trace_id?: string;
}

interface WidgetCustomerEnteredIntentByProactiveBubbleTrackingEvent
  extends BaseTrackingEvent {
  event_type: 'widget-customer-entered-intent-by-proactive-bubble';
  intent_id?: string;
  workflow_id?: string;
}

interface WidgetCustomerEnteredIntentByButtonTrackingEvent
  extends BaseTrackingEvent {
  event_type: 'widget-customer-entered-intent-by-button';
  intent_id?: string;
  workflow_id?: string;
}

interface WidgetCustomerAgentChatTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-customer-agent-chat';
  intent_id?: string;
  workflow_id?: string;
}

interface WidgetCustomerArticleSuggestionHelpedTrackingEvent
  extends BaseTrackingEvent {
  event_type: 'widget-customer-article-suggestion-helped';
  intent_id?: string;
  workflow_id?: string;
}

interface WidgetCustomerArticleSuggestionUnhelpfulTrackingEvent
  extends BaseTrackingEvent {
  event_type: 'widget-customer-article-suggestion-unhelpful';
  intent_id?: string;
  workflow_id?: string;
}

interface WidgetZendeskUpdateSettingsTrackingEvent
  extends BaseTrackingEvent,
    Record<string, unknown> {
  event_type: 'widget-zendesk-update-settings';
}

interface WidgetZendeskAttachmentUploadTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-zendesk-attachment-upload';
  failedReason?: string;
  successfulUploads: number;
}

interface WidgetDisabledByLocalStorageTrackingEvent extends BaseTrackingEvent {
  current_timestamp: number;
  disabled_until_timestamp: number;
  event_type: 'widget-disabled-by-local-storage';
  original_conversation_id: string;
}

export type SendTrackingEventParameters =
  | WidgetCsatSurveyTrackingEvent
  | WidgetOpenedTrackingEvent
  | WidgetCustomerAskQuestionTrackingEvent
  | WidgetCustomerClosedProactiveBubblesTrackingEvent
  | WidgetCustomerProactiveBubblesShownTrackingEvent
  | GoToArticleTrackingEvent
  | WidgetCustomerEnteredIntentByProactiveBubbleTrackingEvent
  | WidgetCustomerEnteredIntentByButtonTrackingEvent
  | WidgetCustomerAgentChatTrackingEvent
  | WidgetCustomerArticleSuggestionHelpedTrackingEvent
  | WidgetCustomerArticleSuggestionUnhelpfulTrackingEvent
  | WidgetZendeskUpdateSettingsTrackingEvent
  | WidgetZendeskAttachmentUploadTrackingEvent
  | WidgetDisabledByLocalStorageTrackingEvent
  | WidgetCsatOpenedTrackingEvent
  | WidgetBannerImageClickedTrackingEvent
  | WidgetBannerImageClosedTrackingEvent
  | WidgetErrorBannerVisibleTrackingEvent;

export type SendTrackingActionArgs = SendTrackingActionCommonArgs &
  SendTrackingEventParameters;

export interface ResetConversationInterface {
  acknowledged: boolean;
  modified_date?: string;
  next_step_id: string;
  transcript_length: number;
}

interface SelectIntentComponent extends BaseWidgetComponent {
  component_fields: {
    custom_greeting?: string;
    language?: string;
    show_greeting_message: boolean;
    show_top_n_intents: boolean;
    translated_greeting_line?: string;
  };
  component_type: ComponentTypes;
  // Adding these for parity with the other component expected types from WidgetComponents to resolve type issues in the reducer. Once we split the reducer into v1 and v2 we can remove these.
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isStreamingComponent?: boolean;
  language?: string;
  query?: string;
}

interface InitIntentInterface {
  // Key should be either intent_id or free_form_query.
  [key: string]: string;
}

interface IntentConversationResponse {
  conversation_id: string;
  intent_id?: string;
  is_csat_promptable?: boolean;
  language?: string | null;
  modified_date?: string;
  next_step_id: string | null;
  preview_logs?: PreviewLog[];
  previous_intent_ids?: Array<string> | null;
  transcript_length: number | null;
  widget_components?: Array<WidgetComponent>;
  workflow_id?: string;
}

interface SubmitInteractiveEmailDeflectionFeedbackResponse {
  redirect_url: string;
}

interface CsatFeedbackRequestFields {
  additional_feedback?: string;
  csat_feedback?: string[];
  csat_resolve?: boolean;
  csat_score?: number;
  csat_trigger_point_prompted?: boolean;
  is_partial_submission: boolean;
  resume_from_trigger_point: boolean;
  routing_intent_workflow_id?: string;
}

function isCsatFeedbackRequestFields(
  fields: CsatFeedbackRequestFields | QuickFeedbackRequestFields,
): fields is CsatFeedbackRequestFields {
  return (
    (fields as CsatFeedbackRequestFields).is_partial_submission !== undefined
  );
}

interface QuickFeedbackRequestFields {
  additionalFeedback: string;
  articleStepData: ArticleStepData | undefined;
  isUseful: boolean;
  knowledgeArticles: BaseArticleComponent[];
  textMessageOverride: string | undefined;
}

interface StreamingResponse {
  modified_date?: string | null;
  next_step_id?: string | null;
  transcript_length?: number | null;
  widget_components: Array<WidgetComponent>;
}

type WidgetConfigRequest = {
  conversation_id: string | undefined;
  raw_params?: {
    [attrName: string]: string | boolean;
  };
};

type PresignedUrlRequest = {
  content_length: number;
  conversation_id: string | undefined;
  file_name: string;
  should_generate_download_link: boolean;
};

type IntentFilters = Record<
  string,
  { is_autoflow: boolean; is_draft: boolean }
>;

type SendFreeFormQueryRequest = {
  component_type?: ComponentTypes;
  intent_filters_for_preview?: IntentFilters | null;
  next_step_id: string | null;
  query: string;
  template_intent_id?: string | null;
  transcript_length: number | null;
};

type RestartConversationRequest = {
  context_variables: Record<string, never>;
  next_step_id: string | null;
  transcript_length: number | null;
};

type RestartIntentConversationRequest = {
  context_variables: Record<string, never>;
  keep_previous_intent_ids?: boolean;
  next_step_id: string | null;
  transcript_length: number | null;
};

type UpdateIntentConversationRequest = {
  context_variables: ContextVariables;
  intent_filters_for_preview?: IntentFilters | null;
  next_step_id: string | null;
  transcript_length: number | null;
};

type SendFeedbackRequest = {
  csat_feedback_fields?: SendCsatFeedbackFields;
  feedback_fields?: SendQuickFeedbackRequest;
};

type SendCsatFeedbackFields = {
  additional_feedback?: string;
  csat_feedback?: string[];
  csat_resolve?: boolean;
  csat_score?: number;
  csat_trigger_point_prompted?: boolean;
  is_partial_submission: boolean;
  resume_from_trigger_point: boolean;
  routing_intent_workflow_id?: string;
};

type SendQuickFeedbackRequest = {
  additional_feedback: string;
  article_step_data: ArticleStepData | undefined;
  is_useful: boolean;

  // Force implementation code to set `full_html: undefined` to avoid sending
  // it to the backend:
  knowledge_articles: (BaseArticleComponent & { full_html: undefined })[];

  text_message_override: string | undefined;
};

type HyperlinkRedirectStepComponent = {
  component_fields: {
    destination_link: string;
    forethought_text: {
      click_here: string;
      redirect_message: string;
      redirecting: string;
    };
    redirect_option: 'current_tab' | 'new_tab';
  };
  component_type: ComponentTypes;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isStreamingComponent?: boolean;
} & BaseWidgetComponent;

export const isButtonsComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is ButtonComponent =>
  widgetComponent.component_type === 'buttons';

const isFormComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is FormStepComponent =>
  widgetComponent.component_type === 'form';

const isTextMessageComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is UserMessageComponent =>
  widgetComponent.component_type === 'text_message';

const isWorkflowTagSelectorComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is ButtonComponent => {
  const componentFields =
    widgetComponent.component_fields as ButtonComponent['component_fields'];
  return (
    (isButtonsComponent(widgetComponent) &&
      componentFields.should_confirm_tag) ??
    false
  );
};

const isSelectIntentComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is SelectIntentComponent =>
  widgetComponent.component_type === 'restart_conversation';

const isZendeskAttachmentUploadStepComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is ZendeskAttachmentUploadStepComponent =>
  widgetComponent.component_type === 'zendesk_attachment_upload';

const isArticleComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is ArticleCarouselComponent =>
  widgetComponent.component_type === 'article' ||
  widgetComponent.component_type === 'article_suggestion';

const isEventMessageComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is EventMessageComponent =>
  widgetComponent.component_type === 'event_message';

const isUserMessageComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is UserMessageComponent =>
  widgetComponent.component_type === 'user_message';

const isButtonStepWithHideFreeFormQueryField = (
  widgetComponent: WidgetComponent,
): widgetComponent is ButtonComponent => {
  const componentFields =
    widgetComponent.component_fields as ButtonComponent['component_fields'];

  return (
    (isButtonsComponent(widgetComponent) &&
      componentFields.hide_free_form_query) ??
    false
  );
};

interface AutonomousAgentConversationResponse {
  intent_id: string;
  intent_title: string;
  internal_output: string[];
  is_workflow_preview: boolean;
  policy: string;
  widget_components: WidgetComponent[];
}

interface InitializeAutonomousAgentConversationRequest {
  conversation_id: string;
  free_form_query: string;
  intent_id?: string | null;
}

interface AutonomousAgentQueryRequest {
  intent_id: string;
  is_workflow_preview: boolean;
  query: string;
}

interface ListOption {
  label: string;
  value: string;
}

type TriggerEventStepContextVariable = {
  default: string | boolean | number;
  id: string;
  name: string;
  type: string;
};

type TriggerEventStepComponent = {
  component_fields: {
    additional_context: TriggerEventStepAdditionalContext;
    context_variables_to_update: TriggerEventStepContextVariable[];
    event_id: string;
    event_name: string;
  };
  component_type: ComponentTypes;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isStreamingComponent?: boolean;
} & BaseWidgetComponent;

interface TriggerEventStepAdditionalContext extends ContextVariables {
  free_form_query: string;
}

interface ImageStepComponent {
  component_fields: {
    images: {
      description: string;
      url: string;
    }[];
  };
  component_type: 'image';
}

interface Card {
  displayable_dynamic_sections: DisplayableDynamicSection[];
  value: string;
}

interface DisplayableDynamicSection {
  components: Component[];
  type: 'image' | 'content' | 'title';
}

interface Component {
  label?: string;
  value: string;
}

interface DynamicCardStepComponent extends BaseWidgetComponent {
  component_fields: {
    cards: Card[];
    content_config: { limit: number } | null;
    forethought_text: {
      next: string;
      previous: string;
      select: string;
      view_less: string;
      view_more: string;
    };
    message: string | null;
    output_variable: string;
    style: 'carousel' | 'stacked-cards';
  };
  component_type: 'dynamic_card';
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isStreamingComponent?: boolean;
  query?: string;
}

interface EmbedStepComponent {
  component_fields: {
    embed_url: string;
  };
  component_type: 'embed';
}

interface CsatTriggerPointStepComponent extends BaseWidgetComponent {
  component_fields: {
    delay: number;
    negative_rating_routing_intent?: string;
    should_use_delay: boolean;
  };
  component_type: 'csat_trigger_point';
}

// preview logs related types
type PreviewLog =
  | ArticleSuggestionStepPreviewLog
  | ApiStepPreviewLog
  | IntentPredictionPreviewLog
  | WarningPreviewLog
  | GotoStepPreviewLog;

interface ArticleSuggestionStepPreviewLog {
  articles_predicted: string[];
  confidence_scores: number[];
  log_type: 'article_suggestion_step';
}

interface WarningPreviewLog {
  log_type: 'warning';
  message?: string;
  title: string;
}

interface ApiStepPreviewLog {
  action_name?: string;
  input_params: Record<string, string>;
  log_type: 'api_step';
  output_params: Record<string, string>;
  url_used?: string;
}

interface IntentPredictionPreviewLog {
  confidence_scores: number[];
  intents_predicted: string[];
  log_type: 'intent_prediction';
}
interface GotoStepPreviewLog {
  intent_id: string;
  intent_title: string;
  log_type: 'goto_step';
}

type WidgetMode = 'workflow' | 'liveChat';

interface LiveChatSendMessageRequest {
  attachments: FileAttachment[];
  message: string;
}

interface SubmitInteractiveEmailDeflectionFeedbackRequest {
  is_deflected: boolean;
  token: string;
}

interface UpdateHelpdeskConversationRequest {
  helpdesk_conversation_id: string;
  include_attachments: boolean | null;
  include_transcript: boolean | null;
  integration: string;
}

interface LiveChatAgentMessageEventData {
  agent: string;
  articles?: LiveChatArticle[];
  attachments?: FileAttachment[];
  avatar: string | undefined;
  message: string;
  type: 'message' | 'event';
}

interface LiveChatArticle {
  link: string;
  preview: string;
  title: string;
}

export interface PersistedWidgetComponent {
  component_attributes: Record<string, unknown>;
  component_fields: Record<string, unknown>;
  component_type: ComponentTypes;
}
interface WidgetComponentsResponse {
  widget_components: Array<PersistedWidgetComponent>;
}

export interface KustomerOneChatOnLoadConfig {
  brand_id: string;
  enabled: boolean;
  jwt: string;
  key: string;
}

export interface OnLoadConfig {
  kustomer_one_chat: KustomerOneChatOnLoadConfig | null;
}

export type {
  AdditionalParameters,
  ApiHeaders,
  ArticleCarouselComponent,
  ArticleComponent,
  AttachmentAnalysisUploadStepComponent,
  AttachmentUploadStepComponent,
  AttemptedToFetchRedactedConversationError,
  AutonomousAgentConversationResponse,
  AutonomousAgentQueryRequest,
  BadRequestError,
  BrandEmbassyCustomFields,
  ButtonComponent,
  ButtonOptions,
  ContextVariables,
  ConversationNotFoundError,
  CsatConfig,
  CsatData,
  CsatFeedbackRequestFields,
  CsatFormData,
  CsatStates,
  CsatTranslatedText,
  CsatTriggerPointStepComponent,
  DropdownItem,
  DynamicCardStepComponent,
  EmbedStepComponent,
  EventMessageComponent,
  FormattedSalesforceExtraPrechatFormDetails,
  FormFields,
  FormStepComponent,
  FormStepComponentText,
  HyperlinkRedirectStepComponent,
  ImageStepComponent,
  InitConversationInterface,
  InitializeAutonomousAgentConversationRequest,
  InitIntentInterface,
  IntentConversationResponse,
  IntentFilters,
  InteractiveEmailInquiryComponent,
  IntercomChatAutoSendConfig,
  KustomerCallbackResponse,
  ListOption,
  LiveChatAgentMessageEventData,
  LiveChatArticle,
  LiveChatSendMessageRequest,
  NewIrArticleComponent,
  OpenUrlComponent,
  PresignedUrlRequest,
  PreviewLog,
  QuickFeedbackRequestFields,
  RestartConversationRequest,
  RestartIntentConversationRequest,
  SalesforceVisibleFields,
  SelectIntentComponent,
  SendFeedbackRequest,
  SendFreeFormQueryRequest,
  StreamingResponse,
  SubmitInteractiveEmailDeflectionFeedbackRequest,
  SubmitInteractiveEmailDeflectionFeedbackResponse,
  TooManyRequestsError,
  TriggerEventStepAdditionalContext,
  TriggerEventStepComponent,
  TriggerEventStepContextVariable,
  UpdateHelpdeskConversationRequest,
  UpdateIntentConversationRequest,
  UserMessageComponent,
  WidgetComponent,
  WidgetComponentsResponse,
  WidgetConfigRequest,
  WidgetMode,
  WorkflowConfigArgs,
  WorkflowConfigResponse,
  ZendeskAttachmentUploadStepComponent,
};

export const invisibleComponentTypes: ComponentTypes[] = [
  // Handoffs:
  'brand_embassy_chat_handoff',
  'drift_chat_handoff',
  'intercom_chat_handoff',
  'kustomer_chat_handoff',
  'salesforce_chat_handoff',
  'snapengage_chat_handoff',
  'zendesk_chat_handoff',
  'zendesk_messaging_handoff',
  'generic_handoff',
  'gorgias_chat_handoff',
  'freshchat_chat_handoff',
  'salesforce_messaging_handoff',
  'live_chat_chat_handoff',
  'ujet_chat_handoff',

  'csat_trigger_point',
  'end_interactive_email_chat',
  'knowledge_retrieval_stream',
  'open_url',
  'trigger_event',
] as const;

export interface Delay {
  delay: number;
  shownAtInMilliseconds: number;
  status: 'not delayed' | 'delaying' | 'delayed';
}

export type Delays = Array<Delay>;

export interface AnswerType extends BaseArticleComponent {
  confidence_level?: string;
  confidence_score?: number;
  date_created?: string;
  date_updated?: string;
  preview: string;
  source_type: string;
}

export {
  BaseWidgetComponent,
  isArticleComponent,
  isAttachmentAnalysisUploadStepComponent,
  isButtonStepWithHideFreeFormQueryField,
  isCsatFeedbackRequestFields,
  isEventMessageComponent,
  isFormComponent,
  isSelectIntentComponent,
  isTextMessageComponent,
  isUserMessageComponent,
  isWorkflowTagSelectorComponent,
  isZendeskAttachmentUploadStepComponent,
};
